import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import {
  MoreHorizontal,
  Eye,
  Clock,
  CheckCircle,
  Trash2,
  ShieldMinus,
  ShieldCheck,
  CheckSquare,
  Search,
  GitPullRequestDraft,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/common/DataTable';
import { Account, DateRange } from '@/types';
import { formatDate } from '@/utils';
import Loader from '@/components/common/Loader';
import { useGetAccountsQuery, useUpdateUserStatusMutation } from '@/store/api/accountsApi';
import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DateRangePicker } from '@/components/common/DateRangePicker';
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

interface KycStatusBadgeProps {
  isVerified: boolean;
}

function UserActions({ user }: { user: Account }) {
  const [updateStatus, { isLoading }] = useUpdateUserStatusMutation();

  const handleStatusUpdate = async (
    payload: { isDeleted?: true } | { status?: 'Active' | 'Blocked' | 'Suspended' }
  ) => {
    try {
      await updateStatus({
        accountId: Number(user.id),
        ...payload,
      }).unwrap();
    } catch (err) {
      console.error('Failed to update user status', err);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-8 h-8 p-0 hover:bg-slate-700"
          aria-label="dropdown "
          disabled={isLoading}
        >
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="w-4 h-4 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="bg-slate-950 border-slate-800 text-slate-200">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-700" />

        {/* DELETE */}
        <DropdownMenuItem
          onClick={() => handleStatusUpdate({ isDeleted: true })}
          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-300"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete user
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-slate-700" />

        {user.status === 'Active' ? (
          <>
            <DropdownMenuItem
              onClick={() => handleStatusUpdate({ status: 'Blocked' })}
              className="hover:bg-slate-700 focus:bg-slate-700"
            >
              <ShieldMinus className="w-4 h-4 mr-2" />
              Block
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => handleStatusUpdate({ status: 'Suspended' })}
              className="hover:bg-slate-700 focus:bg-slate-700"
            >
              <ShieldMinus className="w-4 h-4 mr-2" />
              Suspend
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem
            onClick={() => handleStatusUpdate({ status: 'Active' })}
            className="text-green-400 hover:bg-slate-700 focus:bg-slate-700 focus:text-green-300"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            Activate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type UserStatus = 'Active' | 'Suspended' | 'Blocked';

function StatusBadge({ status }: { status: UserStatus }) {
  const config: Record<
    UserStatus,
    {
      label: string;
      styles: string;
    }
  > = {
    Active: {
      label: 'Active',
      styles: 'bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20',
    },
    Suspended: {
      label: 'Suspended',
      styles: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20 hover:bg-yellow-500/20',
    },
    Blocked: {
      label: 'Blocked',
      styles: 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20',
    },
  };

  const { label, styles } = config[status] || config.Active;

  return (
    <Badge variant="outline" className={`${styles} px-2.5 py-0.5 border`}>
      {label}
    </Badge>
  );
}

export function KycStatusBadge({ status }: { status: string }) {
  if (status === 'Pending') {
    return (
      <Badge variant="outline" className="gap-1 ">
        <Clock className="w-3 h-3" />
        KYC Pending
      </Badge>
    );
  }

  if (status === 'Submitted' || status === 'submitted') {
    return (
      <Badge variant="outline" className="gap-1 text-blue-400 bg-blue-500/10 border-blue-500/20">
        <CheckSquare className="w-3 h-3" />
        KYC Submitted
      </Badge>
    );
  }

  if (status === 'Approved') {
    return (
      <Badge variant="outline" className="gap-1 text-green-400 bg-green-500/10 border-green-500/20">
        <CheckCircle className="w-3 h-3" />
        KYC Approved
      </Badge>
    );
  }

  if (status === 'Rejected' || status === 'rejected') {
    return (
      <Badge variant="outline" className="gap-1 text-red-400 bg-red-500/10 border-red-500/20">
        <ShieldMinus className="w-3 h-3" />
        KYC Rejected
      </Badge>
    );
  }

  if (status === 'Draft') {
    return (
      <Badge variant="outline" className="gap-1 text-white bg-[#8f8c8c21] border-[#8f8f8f]/50">
        <GitPullRequestDraft className="w-3 h-3" />
        KYC Draft
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-slate-500/10 text-slate-400 border-slate-500/20">
      {status || 'N/A'}
    </Badge>
  );
}

export function KybStatusBadge({ status }: { status: string }) {
  if (status === 'Draft' || status === 'draft') {
    return (
      <Badge
        variant="outline"
        className="gap-1 text-yellow-500 bg-yellow-500/10 border-yellow-500/20"
      >
        <Clock className="w-3 h-3" />
        KYB Pending
      </Badge>
    );
  }

  if (status === 'Submitted' || status === 'submitted') {
    return (
      <Badge variant="outline" className="gap-1 text-blue-400 bg-blue-500/10 border-blue-500/20">
        <CheckSquare className="w-3 h-3" />
        KYB Submitted
      </Badge>
    );
  }

  if (status === 'Approved') {
    return (
      <Badge variant="outline" className="gap-1 text-green-400 bg-green-500/10 border-green-500/20">
        <CheckCircle className="w-3 h-3" />
        KYB Approved
      </Badge>
    );
  }

  if (status === 'Rejected' || status === 'rejected') {
    return (
      <Badge variant="outline" className="gap-1 text-red-400 bg-red-500/10 border-red-500/20">
        <ShieldMinus className="w-3 h-3" />
        KYB Rejected
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="bg-slate-500/10 text-slate-400 border-slate-500/20">
      {status || 'N/A'}
    </Badge>
  );
}
export function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const debouncedSearch = useDebounce(search, 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { userType } = useParams();

  useEffect(() => {
    setPageIndex(0);
    setSearch('');
  }, [userType]);

  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });

  const { data, isLoading, isError } = useGetAccountsQuery(
    {
      skip: pageIndex * pageSize,
      take: pageSize,
      search: debouncedSearch,
      accountType: userType,
      kycStatus: statusFilter === 'ALL' ? undefined : (statusFilter as any),
      fromDate: dateRange.from?.toISOString(),
      toDate: dateRange.to?.toISOString(),
    }
    // { refetchOnMountOrArgChange: true }
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0);
  };

  //   if (!kycFilter || kycFilter === 'ALL') return true;

  //   // Map DataTable filter values to API/Data values if necessary
  //   // "Draft" -> "Draft"
  //   // "Submitted" -> "Submitted"
  //   // "Verified" -> "Approved" or "Verified"
  //   // "Rejected" -> "Rejected"

  //   if (kycFilter === 'Verified') {
  //     return u.kycStatus === 'Approved' || u.kycStatus === 'Verified';
  //   }

  //   if (kycFilter === 'Draft') {
  //     return u.kycStatus === 'Draft' || !u.kycStatus;
  //   }

  //   return u.kycStatus === kycFilter;
  // });

  // const totalCount = filteredUsers.length;
  // const users = filteredUsers.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  const requests = data?.data || [];
  const totalCount = data?.total || 0;

  const columns: ColumnDef<Account>[] = [
    {
      header: 'Name',
      cell: ({ row }) => {
        const user = row.original;
        const fullName = `${user.firstname} ${user.lastname}`.trim();
        return (
          <div className="flex items-center space-x-3">
            <div className="h-9 w-9 rounded-full bg-primary p-[2px] shadow-sm">
              <div className="flex items-center justify-center w-full h-full overflow-hidden rounded-full bg-slate-950">
                {user?.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt="profile"
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <span className="text-xs font-bold text-white">
                    {user?.firstname?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="font-semibold text-white">{fullName}</div>
              <div className="text-xs text-slate-400">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      header: 'Mobile No.',
      cell: ({ row }) => <span className="capitalize text-slate-300">{row.original.mobile}</span>,
    },
    {
      header: 'Type',
      cell: ({ row }) => <span className="capitalize text-slate-300">{row.original.type}</span>,
    },
    ...(userType !== 'Company'
      ? [
          {
            id: 'kycStatus',
            header: 'KYC',
            accessorKey: 'kycStatus',
            cell: ({ row }: any) => <KycStatusBadge status={row.original.kycStatus} />,
          },
        ]
      : []),
    ...(userType === 'Company'
      ? [
          {
            id: 'kycStatus',
            header: 'KYC',
            accessorKey: 'kycStatus',
            cell: ({ row }: any) => <KycStatusBadge status={row.original.companyKyc?.kycStatus} />,
          },
        ]
      : []),
    ...(userType === 'Company'
      ? [
          {
            id: 'kybStatus',
            header: 'KYB',
            accessorKey: 'kybStatus',
            cell: ({ row }: any) => <KybStatusBadge status={row.original.companyKyc?.kybStatus} />,
          },
        ]
      : []),
    {
      header: 'Details',
      cell: ({ row }) => (
        <div
          onClick={() => navigate(`/users/${userType}/detail/${row?.original?.id}`)}
          className="flex items-center justify-center w-full"
        >
          <Button
            variant="ghost"
            aria-label="eye "
            size="sm"
            className="w-8 h-8 p-0 hover:bg-blue-500/10 text-slate-400 hover:text-blue-400"
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
    { header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} /> },
    {
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-slate-400">
          {formatDate(new Date(row.original.createdAt))}
        </span>
      ),
    },
    { id: 'actions', header: 'Actions', cell: ({ row }) => <UserActions user={row.original} /> },
  ];

  const handleDelete = (selectedUsers: Account[]) => {
    if (confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
      alert(`${selectedUsers.length} users deleted!`);
    }
  };

  if (isLoading) return <Loader />;
  if (isError) return <div className="p-6 text-red-500">Failed to load users</div>;

  return (
    <div className="p-6 pb-20 space-y-6 animate-fade-in">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-white">User Listing</h2>
          <p className="text-slate-400">Manage your {userType?.toLowerCase()} accounts.</p>
        </div>
        <div className="flex gap-2 p-1 border rounded-lg bg-slate-900/50 border-slate-800">
          <Button
            onClick={() => navigate(`/create-user/${userType}`)}
            aria-label="add user "
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Add User
          </Button>
        </div>
      </div>

      <Card className="shadow-xl bg-slate-900/50">
        <CardContent className="p-0">
          <DataTable
            data={requests}
            columns={columns}
            enableStatusFilter2={true}
            pageSize={pageSize}
            pageIndex={pageIndex}
            kycFilterValue={kycFilter ?? 'ALL'}
            pageCount={Math.ceil((data?.count ?? 0) / pageSize)}
            manualPagination
            onDelete={handleDelete}
            enableSearch={true}
            search={search}
            onSearchChange={handleSearchChange}
            dateRange={dateRange}
            // onDateRangeChange={setDateRange}
            statusFilter={statusFilter}
            onStatusFilterChange={(value) => {
              setStatusFilter(value);
              setPageIndex(0);
            }}
            onDateRangeChange={(range) => {
              setDateRange(range);
              setPageIndex(0);
            }}
            onPaginationChange={(updater) => {
              const next =
                typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
              setPageIndex(next.pageIndex);
              setPageSize(next.pageSize);
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
